import type { ColumnDef } from '@tanstack/react-table';
import { Badge, BadgeColor } from '@/components/Badge';
import type { BadgeOption, TableColumnMeta } from '@/components/table/table.types';

type FacetValue = string | boolean;

export interface ProfileFacet<TData> {
  /** The filter's heading over the facet's options; ungrouped facets share one leading list. */
  group?: string;
  options: BadgeOption<FacetValue>[];
  value: (row: TData) => FacetValue | null;
  /** Whether the facet's badge earns its place in the cell; the filter sees the facet either way. */
  isShown?: (row: TData) => boolean;
}

interface ProfileColumnConfig<TData> {
  id: string;
  header: string;
  size?: number;
  facets: ProfileFacet<TData>[];
  defaultFilter?: FacetValue[];
}

/** One column of badges standing for several facets of a billetterie person, each filterable. */
export function createProfileColumn<TData>({
  id,
  header,
  size,
  facets,
  defaultFilter,
}: ProfileColumnConfig<TData>): ColumnDef<TData> {
  const shownOptions = (row: TData) =>
    facets.flatMap((facet) => {
      if (facet.isShown && !facet.isShown(row)) return [];
      const value = facet.value(row);
      const option = facet.options.find((candidate) => candidate.value === value);
      if (option) return [option];
      return typeof value === 'string' && value
        ? [{ value, label: value, color: BadgeColor.GRAY }]
        : [];
    });

  return {
    id,
    header,
    size,
    accessorFn: (row) =>
      shownOptions(row)
        .map((option) => option.label)
        .join(' '),
    cell: ({ row }) => {
      const options = shownOptions(row.original);
      if (options.length === 0) return '-';
      return (
        <div className="flex gap-1">
          {options.map((option) => (
            <Badge key={String(option.value)} color={option.color}>
              {option.label}
            </Badge>
          ))}
        </div>
      );
    },
    meta: {
      filterType: 'badge-enum',
      options: facets
        .flatMap((facet) => facet.options.map((option) => ({ ...option, group: facet.group })))
        .filter((option, index, all) => all.findIndex((o) => o.value === option.value) === index),
      getFilterValue: (row: TData) => facets.map((facet) => facet.value(row)),
      defaultFilter,
    } satisfies TableColumnMeta<TData, FacetValue>,
  };
}
